import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { fetchGet } from "../../../../service/api_client";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";

export interface StockLedgerFilters {
  search?: string;
  txn_type?: string;
  item_type?: string;
}

export interface StockLedgerPagination {
  page: number;
  totalPages: number;
  total: number;
  limit: number;
}

export interface StockLedgerSummary {
  totalEntries: number;
  inwardValue: number;
  outwardValue: number;
  netMovement: number;
}

export type LedgerRow = {
  id: string;
  date: string;
  docRef: string;
  docType: string;
  itemCode: string;
  itemName: string;
  itemType: string;
  txnType: "IN" | "OUT";
  qty: number;
  rate: number;
  value: number;
  balQty: number;
  balValue: number;
  wacRate: number;
};

interface StockLedgerContextType {
  rows: LedgerRow[];
  summary: StockLedgerSummary;
  isLoading: boolean;
  filters: StockLedgerFilters;
  setFilters: React.Dispatch<React.SetStateAction<StockLedgerFilters>>;
  pagination: StockLedgerPagination;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  refreshLedger: () => Promise<void>;
}

const defaultPagination: StockLedgerPagination = {
  page: 1,
  totalPages: 1,
  total: 0,
  limit: 10,
};

const defaultSummary: StockLedgerSummary = {
  totalEntries: 0,
  inwardValue: 0,
  outwardValue: 0,
  netMovement: 0,
};

const StockLedgerContext = createContext<StockLedgerContextType | undefined>(
  undefined,
);

export const useStockLedger = () => {
  const context = useContext(StockLedgerContext);
  if (!context) {
    throw new Error("useStockLedger must be used within StockLedgerProvider");
  }
  return context;
};

function formatLedgerDate(created_at: any): string {
  if (!created_at) return "-";
  const d =
    typeof created_at === "number" || typeof created_at === "bigint"
      ? new Date(Number(created_at))
      : new Date(created_at);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export const StockLedgerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { showToast } = useToast();
  const [rows, setRows] = useState<LedgerRow[]>([]);
  const [summary, setSummary] = useState<StockLedgerSummary>(defaultSummary);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState<StockLedgerFilters>({
    search: "",
    txn_type: "all",
    item_type: "all",
  });
  const [page, setPage] = useState(1);
  const [pagination, setPagination] =
    useState<StockLedgerPagination>(defaultPagination);

  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearch(filters.search?.trim()),
      400,
    );
    return () => clearTimeout(timer);
  }, [filters.search]);

  const refreshLedger = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      if (debouncedSearch?.trim()) params.set("search", debouncedSearch.trim());
      if (filters.txn_type !== "all") params.set("txn_type", filters.txn_type!);
      if (filters.item_type !== "all")
        params.set("item_type", filters.item_type!);

      const query = params.toString();
      const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/wac${
        query ? `?${query}` : ""
      }`;

      const res = await fetchGet(url);
      if (res?.success && Array.isArray(res.data)) {
        const apiRows = res.data as any[];
        const mapped: LedgerRow[] = apiRows.map((r: any, index: number) => {
          const code: string = r.item_code || "";
          let itemType: string = r.item_subcategory || "";

          if (!itemType && code.length >= 9) {
            const typeCode = code.slice(7, 9).toUpperCase();
            if (typeCode === "FG" || typeCode === "SG" || typeCode === "RM") {
              itemType = typeCode;
            }
          }

          const txnType: "IN" | "OUT" = r.action === "Outward" ? "OUT" : "IN";
          const value: number =
            typeof r.total_price_per_item === "number"
              ? r.total_price_per_item
              : typeof r.total_price === "number"
                ? r.total_price
                : (r.quantity || 0) * (r.price_per_item || 0);

          return {
            id: r._id || String(index),
            date: formatLedgerDate(r.created_at),
            docRef: r.po_no || r.doc_ref || "",
            docType: String(r.po_no || r.doc_ref || "").split("-")[0] || "",
            itemCode: code,
            itemName: r.item_name || "",
            itemType,
            txnType,
            qty: r.quantity ?? 0,
            rate: r.price_per_item ?? 0,
            value,
            balQty: r.closing_stock ?? 0,
            balValue: r.total_price ?? value,
            wacRate: r.wac_value ?? 0,
          };
        });

        const apiSummary = res.summary || {};
        const apiPagination = res.pagination || {};

        setSummary({
          totalEntries: apiSummary.totalEntries || 0,
          inwardValue: apiSummary.inward_sum || 0,
          outwardValue: apiSummary.outward_sum || 0,
          netMovement: apiSummary.net || 0,
        });

        setPagination({
          page: apiPagination.page || page,
          totalPages: apiPagination.totalPages || 1,
          total: apiPagination.total || mapped.length,
          limit: apiPagination.limit || 10,
        });

        setRows(mapped);
      } else {
        setRows([]);
        setSummary(defaultSummary);
        setPagination(defaultPagination);
      }
    } catch (error: any) {
      showToast({
        type: ToastType.error,
        text: error.message || "Failed to load stock ledger",
      });
      setRows([]);
      setSummary(defaultSummary);
    } finally {
      setIsLoading(false);
    }
  }, [page, debouncedSearch, filters.txn_type, filters.item_type, showToast]);

  useEffect(() => {
    refreshLedger();
  }, [refreshLedger]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, filters.txn_type, filters.item_type]);

  const value = useMemo(
    () => ({
      rows,
      summary,
      isLoading,
      filters,
      setFilters,
      pagination,
      page,
      setPage,
      refreshLedger,
    }),
    [rows, summary, isLoading, filters, pagination, page, refreshLedger],
  );

  return (
    <StockLedgerContext.Provider value={value}>
      {children}
    </StockLedgerContext.Provider>
  );
};
